import React, { useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import liff from "../../../lib/liff";
import PotionLayout from "../components/PotionLayout";
import Loading from "default/components/Loading";
import StyledButton from "default/components/StyledButton";
import ButtonDialog from "default/components/ButtonDialog";
import PayPayButton from "default/components/PayPayButton";
import InputEmail from "default/components/InputEmail";
import { useUrlParams } from "hooks/useUrlParams";
import { useTheme } from "@emotion/react";
import { usePotionAssets } from "../hooks/usePotionAssets";
import {
  purchasePotionTamilStripe,
  purchasePotionTamilPaypay,
  getSystemUserEmail,
  updateSystemUserEmail,
} from "utils/api";

const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const PURCHASE_METHODS = {
  STRIPE: "stripe",
  PAYPAY: "paypay",
};

const stripePromise = loadStripe(STRIPE_API_KEY);

const EXCHANGE_OPTIONS = [
  {
    amount: 100,
    price: 100,
    priceWithTax: 110,
    conversations: 20,
    image: "/potion/100TamilImage.png",
  },
  {
    amount: 1000,
    price: 1000,
    priceWithTax: 1100,
    conversations: 200,
    image: "/potion/1000TamilImage.png",
  },
];

function TamilBalanceDisplay({ tamil, theme }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 2,
      }}
    >
      <Typography
        sx={{
          fontFamily: theme.system.fonts.body,
          fontSize: "20px",
          fontWeight: 500,
          lineHeight: "29px",
          textAlign: "center",
          color: theme.system.colors.text.primary,
          WebkitTextStroke: "2px #FFFFFF",
          paintOrder: "stroke fill",
        }}
      >
        所有タミル
      </Typography>
      <Typography
        sx={{
          fontFamily: theme.system.fonts.heading,
          fontSize: "30px",
          fontWeight: 500,
          lineHeight: "35px",
          textAlign: "center",
          color: theme.system.colors.text.primary,
          textDecoration: "none",
          borderBottom: `2px solid ${theme.system.colors.text.primary}`,
          paddingBottom: "4px",
          WebkitTextStroke: "4px #FFFFFF",
          paintOrder: "stroke fill",
        }}
      >
        {tamil}
      </Typography>
    </Box>
  );
}

function ExchangeCard({ option, onExchange, theme }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "360px",
      }}
    >
      {/* Row: coin column (image + text) + button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
          width: "100%",
          justifyContent: "center",
        }}
      >
        {/* Left: coin image + labels below */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}${option.image}`}
            alt={`${option.amount}タミル`}
            sx={{
              width: 144,
              height: 144,
              objectFit: "contain",
            }}
          />
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "24px",
              fontWeight: 700,
              lineHeight: "29px",
              textAlign: "center",
              letterSpacing: "0.1em",
              color: theme.system.colors.text.primary,
              mt: -1,
            }}
          >
            {option.amount}
            <Typography
              component="span"
              sx={{
                fontFamily: theme.system.fonts.body,
                fontSize: "16px",
                fontWeight: 500,
                color: theme.system.colors.text.primary,
              }}
            >
              タミル
            </Typography>
          </Typography>
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "10px",
              fontWeight: 500,
              lineHeight: "20px",
              textAlign: "center",
              letterSpacing: "0.1em",
              color: theme.system.colors.text.primary,
            }}
          >
            +{option.conversations}回の会話量
          </Typography>
        </Box>

        {/* Right: exchange button + tax note */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <StyledButton
            variant="contained"
            onClick={() => onExchange(option.amount)}
            sx={(theme) => ({
              ...theme.components.button,
              minWidth: "auto",
              width: "200px",
              px: 1,
              py: 0.5,
            })}
          >
            {option.price}円と交換
          </StyledButton>
          <Typography
            sx={{
              fontFamily: theme.system.fonts.body,
              fontSize: "10px",
              fontWeight: 500,
              color: theme.system.colors.text.primary,
              mt: 0.5,
            }}
          >
            ※税込{option.priceWithTax}円となります
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function PurchaseDialog({ open, onClose, onPurchaseMethod, isProcessing, lng, t }) {
  const stripeButton = (
    <StyledButton
      key="credit-card"
      variant="contained"
      onClick={() => onPurchaseMethod(PURCHASE_METHODS.STRIPE)}
      sx={(theme) => ({
        width: "100%",
        ...theme.components.button,
      })}
      disabled={isProcessing}
    >
      {t("common:button.purchase_option_credit_card")}
    </StyledButton>
  );

  const payPayBox = (
    <Box key="paypay" sx={{ width: "100%" }}>
      <PayPayButton
        onClick={() => onPurchaseMethod(PURCHASE_METHODS.PAYPAY)}
        disabled={isProcessing}
      />
      <Typography
        sx={{
          mt: 1,
          textAlign: "center",
          whiteSpace: "pre-line",
          fontSize: "12px",
        }}
      >
        {t("common:button.purchase_option_paypay")}
      </Typography>
    </Box>
  );

  const rawButtons = lng === "ja" ? [stripeButton, payPayBox] : [stripeButton];

  return (
    <ButtonDialog
      open={open}
      title={t("common:button.dialog.select_purchase")}
      rawButtons={rawButtons}
      handleClose={onClose}
    />
  );
}

const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

function ExchangeMoney() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { soulId, lng, work } = useUrlParams();
  const { t } = useTranslation([work, "common"]);
  const { tamil, isLoading, isError } = usePotionAssets(soulId);

  const [dialogState, setDialogState] = useState({
    open: false,
    selectedAmount: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState({
    method: null,
    amount: null,
  });
  const [purchaseError, setPurchaseError] = useState(null);

  const purchaseWithStripe = useCallback(
    async (amount) => {
      try {
        const { session_id } = await purchasePotionTamilStripe(soulId, amount);
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: session_id,
        });
        if (error) {
          console.error("Stripe error:", error);
        }
      } catch (error) {
        console.error("Purchase error:", error);
      }
    },
    [soulId]
  );

  const purchaseWithPayPay = useCallback(
    async (amount) => {
      try {
        const result = await purchasePotionTamilPaypay(soulId, amount);
        window.location.href = result.url;
      } catch (error) {
        if (error.status === 409) {
          setPurchaseError("この購入は既に処理されています");
        } else if (error.status === 422) {
          setPurchaseError("入力内容に誤りがあります");
        } else {
          setPurchaseError("購入処理中にエラーが発生しました。しばらく時間をおいて再度お試しください。");
        }
      }
    },
    [soulId]
  );

  const handleExchange = useCallback((amount) => {
    setDialogState({ open: true, selectedAmount: amount });
  }, []);

  const handlePurchaseMethod = useCallback(
    async (method) => {
      const amount = dialogState.selectedAmount;
      setDialogState((prev) => ({ ...prev, open: false }));
      setIsProcessing(true);

      try {
        const email = await getSystemUserEmail();

        if (!email.email_address) {
          setEmailRequired(true);
          setPendingPurchase({ method, amount });
          setIsProcessing(false);
          window.scrollTo(0, 0);
          return;
        }

        if (method === PURCHASE_METHODS.STRIPE) {
          await purchaseWithStripe(amount);
        } else if (method === PURCHASE_METHODS.PAYPAY) {
          await purchaseWithPayPay(amount);
        }
      } catch (error) {
        console.error("Purchase method error:", error);
      } finally {
        setIsProcessing(false);
      }
    },
    [dialogState.selectedAmount, purchaseWithStripe, purchaseWithPayPay]
  );

  const navigateAfterEmail = useCallback(async () => {
    const { method, amount } = pendingPurchase;
    if (method === PURCHASE_METHODS.STRIPE) {
      await purchaseWithStripe(amount);
    } else if (method === PURCHASE_METHODS.PAYPAY) {
      purchaseWithPayPay(amount);
    }
  }, [pendingPurchase, purchaseWithStripe, purchaseWithPayPay]);

  if (isLoading) {
    return (
      <PotionLayout width="100%">
        <Loading />
      </PotionLayout>
    );
  }

  if (isError) {
    return (
      <PotionLayout width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: "100vw",
              position: "relative",
              left: "50%",
              transform: "translateX(-50%)",
              overflow: "hidden",
              boxShadow: "0px 8px 16px rgba(0,0,0,0.3)",
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/potion/MainImageOfExchangePage.png`}
              alt="Exchange"
              width="100%"
              style={{ display: "block" }}
            />
          </Box>
          <Box sx={{ mt: 3, mb: 2 }}>
            <TamilBalanceDisplay tamil="--" theme={theme} />
          </Box>
        </Box>
      </PotionLayout>
    );
  }

  if (emailRequired) {
    return (
      <PotionLayout width="88%">
        <InputEmail soulId={soulId} onClickPurchase={navigateAfterEmail} updateEmailFn={updateSystemUserEmail} />
      </PotionLayout>
    );
  }

  return (
    <PotionLayout width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Title */}
        <Typography
          sx={(theme) => ({
            mt: 3,
            ...theme.components.sectionHeader,
          })}
        >
          E<span style={{ color: "#8CB808" }}>x</span>change
        </Typography>

        {/* Hero Image */}
        <Box
          sx={{
            width: "100vw",
            position: "relative",
            left: "50%",
            transform: "translateX(-50%)",
            overflow: "hidden",
            mt: 3,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/potion/MainImageOfExchangePage.png`}
            alt="Exchange"
            width="100%"
            style={{ display: "block" }}
          />
        </Box>

        {/* Tamil Balance */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <TamilBalanceDisplay tamil={tamil} theme={theme} />
        </Box>

        {/* Exchange Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
            width: "100%",
            px: 3,
            mb: 4,
          }}
        >
          {EXCHANGE_OPTIONS.map((option) => (
            <ExchangeCard
              key={option.amount}
              option={option}
              onExchange={handleExchange}
              theme={theme}
            />
          ))}
        </Box>

        {/* カエデにタミルを支払う */}
        <Box sx={{ px: 3, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", mt: 8, mb: 4, gap: 2 }}>
          <StyledButton
            variant="contained"
            onClick={() => navigate(withLng(`/${work}/payment?soul_id=${soulId}`, lng))}
            sx={(theme) => ({
              width: "100%",
              maxWidth: "300px",
              ...theme.components.button,
            })}
          >
            カエデにタミルを支払う
          </StyledButton>

          {/* チャットに戻る */}
          <StyledButton
            variant="contained"
            onClick={() => liff.closeWindow()}
            sx={(theme) => ({
              width: "100%",
              maxWidth: "300px",
              ...theme.components.button,
            })}
          >
            {t("common:button.back_to_chat")}
          </StyledButton>
        </Box>
      </Box>

      {/* Purchase Dialog */}
      <PurchaseDialog
        open={dialogState.open}
        onClose={() => setDialogState((prev) => ({ ...prev, open: false }))}
        onPurchaseMethod={handlePurchaseMethod}
        isProcessing={isProcessing}
        lng={lng}
        t={t}
      />

      {/* Purchase Error Dialog */}
      <ButtonDialog
        open={!!purchaseError}
        title={purchaseError}
        buttons={[{ label: "閉じる", onClick: () => setPurchaseError(null) }]}
        handleClose={() => setPurchaseError(null)}
      />
    </PotionLayout>
  );
}

export default ExchangeMoney;
